import { memo, useContext, useEffect, useRef, useState, useMemo, useCallback } from "react"
import type { Participant } from "@vc/models"
import { cn } from "@vc/lib/utils"
import { getAvatarColor } from "@vc/utils/avatarColors"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/clientWeb/components/ui/dropdown-menu"
import { Pin, Maximize2, Minimize2, MoreVertical, Volume2, UserMinus, Video, Mic, MessageCircle, Volume1, Monitor, MonitorOff, Crown, Sparkles, X, Activity, PenLine } from "lucide-react"
import { AdvancedStatsOverlay } from "@vc/views/vcLayout/components/overlays/AdvancedStatsOverlay"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/clientWeb/components/ui/tooltip"


import { VirtualBackgroundStream } from "@vc/views/vcLayout/components/video/VirtualBackgroundStream"
import { Switch } from "@/clientWeb/components/ui/switch"
import { Slider } from "@/clientWeb/components/ui/slider"
import { BottomSheet } from "@/clientWeb/components/ui/bottom-sheet"
import { useMediaStream } from "@vc/contexts/MediaStreamContext"
import { deviceManager } from "@/features/devices/services/device-manager"
import { trackFacesAway } from "@/features/devices/services/camera-facing"
import { useRtpStats } from "@/features/meeting/hooks/useRtpStats"
import { statsKey, tileStatsTarget } from "@/features/meeting/services/rtp-stats"
import { reportSize, dropReporter, layerKeyForTile } from "@/features/meeting/services/layer-preference"
import * as renderedTiles from "@/features/meeting/services/rendered-tiles"
import { FULLSCREEN_KEY_ATTR } from "@/features/meeting/services/fullscreen-focus"
import { watchFrameStall } from "@/features/meeting/services/frame-stall"
import { vclog } from "@/features/meeting/hooks/vc-log"
import { useRemoveVideoInput } from "@vc/contexts/VideoInputContext"
import { useResponsiveDevice } from "@vc/viewmodels/hooks/useResponsiveDevice"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useMeetingPreferencesStore } from "@/features/meeting/store/meeting-preferences.store"
import { useMeetingSessionStore } from "@/features/meeting/store/meeting-session.store"
import { ReactionFloaters } from "./ReactionFloaters"
import { useTrackStream } from "@/features/meeting/hooks/useTrackStream"
import { useFullscreen } from "@vc/viewmodels/screen/useFullscreen"
import { VideoFitDefaultContext, type VideoFitMode } from "./video-fit"

/**
 * Report a `<video>` as ready once it has a decoded frame to show.
 *
 * Readiness used to mean `play()` resolved, which happens before the first
 * frame is decoded — so the element faded in black. `loadeddata` is the first
 * frame being available. It is deliberately not `requestVideoFrameCallback`:
 * that one fires on frames being *presented*, and the element is transparent
 * until it is ready, which a browser may decline to present at all.
 */
function watchFirstFrame(el: HTMLVideoElement, onReady: (ready: boolean) => void): () => void {
  if (el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    onReady(true)
    return () => {}
  }
  const handle = () => onReady(true)
  el.addEventListener("loadeddata", handle, { once: true })
  return () => el.removeEventListener("loadeddata", handle)
}

// Memoized camera stream component - only for local participant
const CameraStream = memo(function CameraStream({
  className,
  onReady,
  videoFitMode,
  tileId,
}: {
  className?: string
  onReady: (ready: boolean) => void
  videoFitMode?: "fit" | "fill"
  /** Lets the local recorder composite from this already-decoded element
   *  instead of decoding the stream a second time. */
  tileId?: string
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const { getLocalStream, releaseLocalStream, subscribeLocalStream } = useMediaStream()

  useEffect(() => {
    let mounted = true
    let stopWatching: (() => void) | null = null
    // A swap is always newer than the acquire, so once one has landed a late
    // acquire result must not put the old (now ended) camera back.
    let swapped = false

    const show = (stream: MediaStream) => {
      const el = videoRef.current
      if (!mounted || !el) return
      stopWatching?.()
      el.srcObject = stream
      stopWatching = watchFirstFrame(el, onReady)
    }

    // Changing camera mid-call stops the track this element is showing, and so
    // does the camera being unplugged and reopened on another device. Without
    // following the swap the tile stayed blank — for you only, while the room
    // still saw you — until the camera was toggled off and on, which remounts
    // this component and acquires afresh.
    //
    // A null is the camera closing; that also unmounts this tile (camera off) or
    // is followed by a replacement, so there is nothing to show in between.
    //
    // No fade on the way across: the manager only hands over a camera that is
    // already producing frames, and keeps the old one running until we have
    // moved, so the picture changes in place.
    const unsubscribe = subscribeLocalStream((stream) => {
      if (!stream) return
      swapped = true
      show(stream)
    })

    getLocalStream()
      .then((stream) => {
        if (swapped) return
        show(stream)
      })
      .catch(() => {
        if (!mounted) return
        onReady(false)
      })

    return () => {
      mounted = false
      unsubscribe()
      stopWatching?.()
      releaseLocalStream()
      // The tile's readiness outlives this element (it is tile state), so say
      // so when the element goes — otherwise turning the camera back on shows
      // the new, still-empty element at full opacity.
      onReady(false)
    }
  }, [getLocalStream, releaseLocalStream, subscribeLocalStream, onReady])



  return (
    <div className={cn("h-full w-full", videoFitMode === "fit" && "bg-vc-surface")}>
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        data-pip-video="true"
        data-tile-id={tileId}
        className={cn(
          "h-full w-full",
          videoFitMode === "fit" ? "object-contain" : "object-cover",
          className
        )}
      />
    </div>
  )

})



// Wrapper component — resolves the ref-counted shared local stream and
// hands the resolved `MediaStream` to `VirtualBackgroundStream`. The stream
// is released when the wrapper unmounts so the MediaStream context's
// ref-count stays accurate.
const VirtualBackgroundStreamWrapper = memo(function VirtualBackgroundStreamWrapper({
  backgroundUrl,
  videoFitMode,
  className,
  onReady,
  tileId,
}: {
  backgroundUrl: string
  videoFitMode?: "fit" | "fill"
  className?: string
  onReady: (ready: boolean) => void
  /** See CameraStream.tileId — the recorder composites from the cutout canvas. */
  tileId?: string
}) {
  const { getLocalStream, releaseLocalStream, subscribeLocalStream } = useMediaStream()
  const [stream, setStream] = useState<MediaStream | null>(null)

  useEffect(() => {
    let cancelled = false
    let swapped = false
    // Same camera-switch gap as CameraStream. A new stream identity is what
    // makes VirtualBackgroundStream re-point MediaPipe at the new camera.
    const unsubscribe = subscribeLocalStream((s) => {
      if (!s || cancelled) return
      swapped = true
      setStream(s)
    })
    void getLocalStream()
      .then((s) => {
        if (!cancelled && !swapped) setStream(s)
      })
      .catch(() => {
        if (!cancelled) setStream(null)
      })
    return () => {
      cancelled = true
      unsubscribe()
      releaseLocalStream()
    }
  }, [getLocalStream, releaseLocalStream, subscribeLocalStream])

  return (
    <VirtualBackgroundStream
      backgroundUrl={backgroundUrl}
      stream={stream}
      videoFitMode={videoFitMode}
      className={className}
      onReady={onReady}
      tileId={tileId}
    />
  )
})

/** Distinguishes two <video> elements showing the same source. */
let reporterSeq = 0

const RemoteVideoStream = memo(function RemoteVideoStream({
  stream,
  className,
  onReady,
  videoFitMode,
  tileId,
  layerKey,
  onStalled,
}: {
  stream: MediaStream
  className?: string
  onReady: (ready: boolean) => void
  videoFitMode?: "fit" | "fill"
  /** See CameraStream.tileId — the recorder composites from this element. */
  tileId?: string
  /** `statsKey` triple for this source, so the SFU can be told which simulcast
   *  rung this tile's size actually needs. Absent for the local preview, which
   *  has no consumer to downgrade. */
  layerKey?: string | null
  /** Told when the picture stops moving, and when it moves again — see
   *  `frame-stall`. Pass it for remote CAMERAS only: a still screen share sends
   *  no frames on purpose. */
  onStalled?: (stalled: boolean) => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  // The video tracks alone. A peer's stream also carries their microphone and
  // is re-created whenever either set of tracks changes; bound to that, the
  // tile went blank because someone's AUDIO changed.
  const videoStream = useTrackStream(stream, "video")

  useEffect(() => {
    const el = videoRef.current
    if (!el || !videoStream) return
    el.srcObject = videoStream
    el.muted = true
    const stopWatching = watchFirstFrame(el, onReady)
    el.play().catch(() => {
      // Autoplay blocked — muted play should always succeed; retry once
      el.muted = true
      el.play().catch(() => onReady(false))
    })
    return () => {
      stopWatching()
      el.pause()
      el.srcObject = null
      onReady(false)
    }
  }, [videoStream, onReady])

  useEffect(() => {
    const el = videoRef.current
    if (!el || !videoStream || !onStalled) return
    return watchFrameStall(el, { onChange: onStalled })
  }, [videoStream, onStalled])

  // Tell the SFU how big we are actually drawing this, so it can send the rung
  // that fits instead of the largest one the connection happens to allow.
  //
  // Observed on the <video> itself rather than the tile wrapper: `absolute
  // inset-0` puts the element's border box exactly on the tile box, and every
  // cause of a size change — window resize, panel opening, participant count,
  // layout switch — lands here with no layout state to keep in sync.
  useEffect(() => {
    const el = videoRef.current
    if (!el || !layerKey) return
    // Unique per element, stable for its lifetime: one source can be drawn in
    // two places at once and the widest must win.
    const reporterId = `${layerKey}#${++reporterSeq}`
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width
      if (w) reportSize(layerKey, reporterId, Math.round(w))
    })
    observer.observe(el)
    reportSize(layerKey, reporterId, Math.round(el.getBoundingClientRect().width))
    return () => {
      observer.disconnect()
      dropReporter(layerKey, reporterId)
    }
  }, [layerKey])

  return (
    <div className={cn("h-full w-full", videoFitMode === "fit" && "bg-vc-surface")}>
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        disablePictureInPicture
        data-tile-id={tileId}
        className={cn(
          "h-full w-full",
          videoFitMode === "fit" ? "object-contain" : "object-cover",
          className,
        )}
      />
    </div>
  )
})


/**
 * Whether this client's own camera — the shared capture, not an extra one —
 * points away from the user (a phone's back camera). Followed across device
 * switches; reading it never opens or holds the camera.
 */
function useOwnCameraFacesAway(enabled: boolean): boolean {
  const [facesAway, setFacesAway] = useState(() => enabled && trackFacesAway(deviceManager.getTrack('video')))
  useEffect(() => {
    if (!enabled) {
      setFacesAway(false)
      return
    }
    setFacesAway(trackFacesAway(deviceManager.getTrack('video')))
    return deviceManager.subscribe('video', (track) => setFacesAway(trackFacesAway(track)))
  }, [enabled])
  return facesAway
}

export const VideoTile = memo(function VideoTile({

  participant,

  className,

  reaction,

  isPinned,

  onPin,

  onPinForAll,

  pinnedForAllId,

  onMaximize,

  onOpenChat,

  onMuteParticipant: _onMuteParticipant,

  onRemoveParticipant,

  onChangeRole,

  onStopAudio,

  onStopVideo,

  onStopScreenShare,

  onRequestVideo,
  onRequestAudio,

  virtualBackgroundUrl,

  showMenu = true,

  autoHideNames = false,

}: {

  participant: Participant

  className?: string

  reaction?: string | null

  isPinned?: boolean

  onPin?: (participantId: string) => void

  /**
   * Pin this tile on everyone's screen. Passed only to a moderator, so its
   * presence is what offers "Pin for all".
   */
  onPinForAll?: (participantId: string) => void

  /** The tile a moderator pinned for everyone, if any. */
  pinnedForAllId?: string | null

  onMaximize?: (element: HTMLElement) => void

  onOpenChat?: (participantId: string) => void

  onMuteParticipant?: (participantId: string) => void

  onRemoveParticipant?: (participantId: string) => void

  onAddToSpotlight?: (participantId: string) => void

  onStopAudio?: (participantId: string) => void

  onStopVideo?: (participantId: string) => void

  onStopScreenShare?: (participantId: string) => void

  onRequestAudio?: (participantId: string) => void

  onRequestVideo?: (participantId: string) => void

  onChangeRole?: (participantId: string, role: string) => void

  virtualBackgroundUrl?: string | null

  showMenu?: boolean

  autoHideNames?: boolean

}) {

  // An extra camera tile — labelled with the owner's name plus the device, and
  // stripped of the per-person chrome (mic bars, hand raise, moderation): those
  // all belong to the owner's own tile, not to their second camera.
  const isAdditionalSource = participant.sourceKind === "additional"
  // Null outside a meeting, so a preview render simply offers no close control.
  const removeVideoInput = useRemoveVideoInput()

  // Local participant: use MediaStreamContext (shared camera stream)
  // Remote participant: use participant.stream (real SFU consumer stream, if available)
  //
  // An explicit `stream` always wins, local or not. Your own additional camera
  // is a local tile that carries its own capture; routing it through
  // MediaStreamContext would render your primary camera in both tiles.
  const shouldRenderVideo = participant.isLocal && !participant.stream
    ? participant.videoOn
    : participant.videoOn && !!participant.stream

  // On screen is a fact about the tile, so the tile is what declares it —
  // nothing re-derives which peers are visible, because VideoGrid's own paging
  // is the only place that knows.
  //
  // NOT gated on shouldRenderVideo: a camera-off tile still occupies a slot and
  // must keep its stream, or turning the camera back on could never be seen —
  // the resume would have to come from a stream we had already paused.
  const renderKey = layerKeyForTile(participant)
  useEffect(() => renderedTiles.retain(renderKey), [renderKey])

  const [hasVideo, setHasVideo] = useState(false)
  /** Per tile, and off by default: collection costs nothing until it is open. */
  const [statsOpen, setStatsOpen] = useState(false)

  // Mirror only YOUR OWN primary camera, and only the media layer — mirroring
  // the tile would reverse the name badge and controls with it. Remote peers
  // are never mirrored: this is about how you see yourself, and nothing about
  // the published track changes. Extra cameras are excluded because a second
  // camera is usually pointed at something (a document, a whiteboard) where a
  // mirror image is simply wrong.
  //
  // And only while the camera faces you. On a phone, switching to the back
  // camera kept the mirror, so the room came out reversed, text and all.
  const mirrorOwnVideo = useMeetingPreferencesStore((s) => s.mirrorOwnVideo)
  const ownCameraFacesAway = useOwnCameraFacesAway(participant.isLocal && !isAdditionalSource)
  const mirrorSelf = participant.isLocal && !isAdditionalSource && mirrorOwnVideo && !ownCameraFacesAway

  const [menuOpen, setMenuOpen] = useState(false)



  const [_volume, _setVolume] = useState(50)

  // Derived, not copied.
  //
  // This was `useState(participant.role === "moderator")`, which snapshots the
  // role at mount and never hears about it again — so promoting someone from
  // the Participants list left this tile still offering to promote them, and
  // the toggle below then computed the "new" role from that stale answer and
  // sent the one they already had. The switch was never told its state at all.
  const isModerator = participant.role === "moderator" || participant.role === "host"

  // Whether the person looking may act on the person shown. Same permission the
  // Participants list applies, published by MeetingScreen so the two cannot
  // drift; false until it says otherwise, so a tile never offers moderation on
  // the strength of not knowing.
  const canModerate = useMeetingSessionStore((s) => s.canModerate)
  // Only the host acts on the host — see ParticipantsPanel `canActOn` and the
  // server's `protect_host`. A co-host gets no moderation items on the host.
  const isRoomHost = useMeetingSessionStore((s) => s.isRoomHost)
  const canActOnThis = canModerate && (participant.role !== "host" || isRoomHost)

  // "Pin for all" has nobody to reach while the viewer is alone in the room.
  const hasOtherParticipants = useMeetingSessionStore((s) => s.hasOtherParticipants)

  // The actions themselves. An explicitly passed prop still wins; everything
  // else falls back to what the meeting published, which is how these items do
  // anything at all — no render site passes them.
  const roomActions = useMeetingSessionStore((s) => s.participantActions)
  const stopAudio = onStopAudio ?? roomActions.muteParticipant
  const stopVideo = onStopVideo ?? roomActions.stopVideo
  const stopScreenShare = onStopScreenShare ?? roomActions.stopScreenShare
  const requestAudio = onRequestAudio ?? roomActions.requestAudio
  const requestVideo = onRequestVideo ?? roomActions.requestVideo
  const removeParticipant = onRemoveParticipant ?? roomActions.removeParticipant
  const changeRole = onChangeRole ?? roomActions.changeRole
  const setAnnotator = roomActions.setAnnotator

  const isPresenter = participant.role === "presenter"

  // Show the whole camera frame, whatever shape the tile happens to be.
  //
  // Every tile renders its video with `object-cover` unless this says "fit", and
  // this said "fill" for all of them. `cover` fills the box by cropping to the
  // shorter axis, which is invisible while a tile is roughly 16:9 and ruinous as
  // soon as it is not — a rail beside a pinned participant, a two-up column, a
  // narrow phone tile. Those keep the height and throw the sides away, so people
  // were trimmed to a strip of face with both edges gone.
  //
  // Letterboxing is the honest trade: a tile that does not match the camera's
  // aspect shows bars rather than silently hiding part of the person. Anyone who
  // prefers the crop still has Fill in the tile's own menu.
  //
  // Where a tile STARTS is the layout's call (VideoFitDefaultContext): the
  // stage rails are designed with tiles that fill their rows, so they start in
  // Fill; everywhere else starts in Fit. Either way the menu switches it.
  const defaultFitMode = useContext(VideoFitDefaultContext)
  const [videoFitMode, setVideoFitMode] = useState<VideoFitMode>(defaultFitMode)

  const [showName, setShowName] = useState(true)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mouseMoveThrottleRef = useRef<number | null>(null)

  const tileRef = useRef<HTMLDivElement>(null)
  // Read only to pick the icon beside the menu; the meeting's onMaximize still
  // does the entering and leaving.
  const { isFullscreen: tileIsFullscreen } = useFullscreen(tileRef)
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { isMobileDevice, isTabletDevice } = useResponsiveDevice()
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false)
  const t = useTranslation();

  // A picture is on screen: something to render, and its first frame decoded.
  const videoVisible = shouldRenderVideo && hasVideo

  // A remote camera whose frames have stopped arriving — the person's phone
  // lost its camera in the background and could not tell the room. The avatar
  // goes over the stale frame until pictures move again. See `frame-stall`.
  //
  // Remote primary cameras only: a screen share or a gateway endpoint can be
  // still on purpose, and an extra camera is often pointed at something still.
  const watchForStall =
    !participant.isLocal && !isAdditionalSource && (participant.videoSourceType ?? "camera") === "camera"
  const [stalled, setStalled] = useState(false)
  const handleStalled = useCallback((next: boolean) => {
    vclog("tile", next ? "remote camera FROZEN — avatar over the last frame" : "remote camera moving again", {
      peer: participant.sfuPeerId ?? participant.id,
    })
    setStalled(next)
  }, [participant.sfuPeerId, participant.id])
  const frozen = watchForStall && stalled && videoVisible

  // One tile can be handed a different person — the sidebar stage follows the
  // speaker, and keeps its element while doing so. Menus opened for whoever
  // was there must not stay open over whoever is there now.
  const [menusFor, setMenusFor] = useState(participant.id)
  if (menusFor !== participant.id) {
    setMenusFor(participant.id)
    setMenuOpen(false)
    setStatsOpen(false)
    setMobileActionsOpen(false)
  }

  // Auto-hide names logic
  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [])

  const scheduleHideName = useCallback(() => {
    if (!autoHideNames) return
    clearHideTimeout()
    hideTimeoutRef.current = setTimeout(() => {
      setShowName(false)
    }, 3000) // Hide after 3 seconds of inactivity
  }, [autoHideNames, clearHideTimeout])

  const handleMouseMove = useCallback(() => {
    if (!autoHideNames) return
    if (mouseMoveThrottleRef.current) return
    mouseMoveThrottleRef.current = window.setTimeout(() => {
      mouseMoveThrottleRef.current = null
    }, 200)
    setShowName(true)
    scheduleHideName()
  }, [autoHideNames, scheduleHideName])

  const handleMouseEnter = useCallback(() => {
    if (!autoHideNames) return
    setShowName(true)
  }, [autoHideNames])

  const handleMouseLeave = useCallback(() => {
    if (!autoHideNames) return
    scheduleHideName()
  }, [autoHideNames, scheduleHideName])

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }, [])

  const handleTileTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!showMenu || !isMobileDevice) return
    if (e.touches.length !== 1) return
    clearLongPressTimer()
    longPressTimerRef.current = setTimeout(() => {
      setMobileActionsOpen(true)
    }, 450)
  }, [showMenu, isMobileDevice, clearLongPressTimer])

  const handleTileTouchMove = useCallback(() => {
    clearLongPressTimer()
  }, [clearLongPressTimer])

  const handleTileTouchEnd = useCallback(() => {
    clearLongPressTimer()
  }, [clearLongPressTimer])

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      clearHideTimeout()
      clearLongPressTimer()
      if (mouseMoveThrottleRef.current) clearTimeout(mouseMoveThrottleRef.current)
    }
  }, [clearHideTimeout, clearLongPressTimer])

  // Reset showName when autoHideNames changes
  useEffect(() => {
    if (!autoHideNames) {
      setShowName(true)
      clearHideTimeout()
    } else {
      scheduleHideName()
    }
  }, [autoHideNames, clearHideTimeout, scheduleHideName])



  // Handle pin action

  const handlePin = useCallback(() => {

    onPin?.(participant.id)

    setMenuOpen(false)

  }, [onPin, participant.id])



  const isPinnedForAll = !!pinnedForAllId && pinnedForAllId === participant.id

  const handlePinForAll = useCallback(() => {
    onPinForAll?.(participant.id)
    setMenuOpen(false)
  }, [onPinForAll, participant.id])

  // Handle maximize action

  const handleMaximize = useCallback(() => {

    if (tileRef.current) {

      onMaximize?.(tileRef.current)

    }

    setMenuOpen(false)

  }, [onMaximize])



  const handleOpenChat = useCallback(() => {

    onOpenChat?.(participant.id)

    setMenuOpen(false)

  }, [onOpenChat, participant.id])



  // Generate deterministic pastel background color for this tile based on participant name
  const backgroundStyle = useMemo(() => {
    const color = getAvatarColor(participant.name)
    return { backgroundColor: color }
  }, [participant.name])



  const initials = participant.name

    .split(" ")

    .filter(Boolean)

    .slice(0, 2)

    .map((w) => w[0]?.toUpperCase())

    .join("")



  // Speaking animation: was always true for local → infinite speakingPulse on every local tile.
  // Now correctly tied to isSpeaking so only fires when participant is actively speaking.
  // An extra camera carries no audio, so it can never be the speaker — the ring
  // belongs on its owner's tile.
  //
  // Everyone, not just you: a remote speaker gets the same ring your own tile
  // does. It was local-only because nothing set a remote `isSpeaking` — now
  // MeetingScreen derives it from the active speaker, so at most one tile in
  // the room pulses at a time.
  const isSpeakingTemp = participant.isSpeaking && !isAdditionalSource



  const isActiveSpeaker = participant.isSpeaking && !isAdditionalSource



  const roleLabel =

    participant.role === "host"

      ? t("videoTile.host")

      : participant.role === "moderator"

        ? t("videoTile.coHost")

        : participant.role === "presenter"

          ? t("videoTile.presenterRole")

          : ""

  const shouldShowThreeDotMenu = showMenu && !isMobileDevice
  const menuTriggerContainerClass = isTabletDevice
    ? "absolute right-3 bottom-3 z-30 flex items-center gap-2 opacity-100"
    : "absolute right-3 bottom-3 z-30 flex items-center gap-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100"



  const qualityLabel =

    participant.connectionQuality === "good"

      ? t("videoTile.good")

      : participant.connectionQuality === "fair"

        ? t("videoTile.fair")

        : participant.connectionQuality === "poor"

          ? t("videoTile.poor")

          : ""



  return (

    <div

      ref={tileRef}

      /* Read when this tile is made fullscreen: its stream stays live and
         every other camera is paused. Empty for a tile with no remote camera
         (your own), which keeps none. See fullscreen-focus. */
      {...{ [FULLSCREEN_KEY_ATTR]: renderKey ?? "" }}

      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTileTouchStart}
      onTouchMove={handleTileTouchMove}
      onTouchEnd={handleTileTouchEnd}
      onTouchCancel={handleTileTouchEnd}

      className={cn(

        "group relative h-full w-full overflow-hidden rounded-[12px]",

        // PERF: transition-all was 500ms on ALL props → INP=528ms. Only transition box-shadow+outline.
        // box-shadow and outline are composited and don't cause layout/paint on the main thread.
        "transition-[box-shadow,outline-color] duration-300 ease-in-out",

        isSpeakingTemp && "ring-6 ring-green-700 vc-speaking-ring",

        isActiveSpeaker && "ring-2 ring-emerald-300/70",

        "hover:shadow-[inset_0_0_0_3px_rgba(6,182,212,0.8)]",

        className,

      )}

      style={backgroundStyle}

    >

      {/* Subtle scrims for Meet/Teams style contrast */}

      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/35 via-transparent to-transparent" />



      {/* Reference-style overlays (non-breaking additions) */}
      {/* Bounded top-to-bottom, not just anchored at the top. The stats panel
          inside sizes itself with `max-h-full`, which resolves against THIS
          box — left at `height:auto` it meant "as tall as the content", i.e.
          no limit at all, and the tile's `overflow-hidden` then cut the panel
          off on a short tile with no way to scroll to the rest.

          `pointer-events-none` because the box now spans the whole tile
          height: without it this invisible column would swallow clicks on the
          video behind it. The panel re-enables them for itself. */}
      <div className="pointer-events-none absolute bottom-2 left-2 top-2 z-20 flex flex-col gap-1 sm:bottom-3 sm:left-3 sm:top-3 sm:gap-2">
        {/* Real connection stats, opened from this tile's menu. Mounted only
            when shown — subscribing is what starts the polling. */}
        {statsOpen && <TileStats participant={participant} />}
      </div>



      {participant.handRaised && !isAdditionalSource && (

        <div className="absolute left-3 top-3 z-10">

          <div className="relative">

            <div className="absolute inset-0 rounded-full bg-amber-400/25 blur-md" />

            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-black/55 ring-1 ring-white/20">

              <span className="text-2xl animate-[handWave_900ms_ease-in-out_infinite]">✋</span>

            </div>

          </div>

        </div>

      )}

      {showMenu && isMobileDevice && (
        <BottomSheet
          open={mobileActionsOpen}
          onClose={() => setMobileActionsOpen(false)}
          title={`${participant.name} ${t("videoTile.options")}`}
          className="max-h-[70vh]"
        >
          <div className="space-y-2 pb-2">
            <button
              type="button"
              onClick={() => {
                handlePin()
                setMobileActionsOpen(false)
              }}
              className="flex w-full items-center gap-2 rounded-lg border border-border px-3 py-2 text-left text-sm text-foreground"
            >
              <Pin className={cn("h-4 w-4", isPinned && "fill-current text-primary")} />
              <span>{isPinned ? t("videoTile.unpinTile") : t("videoTile.pinTile")}</span>
            </button>

            {onPinForAll && hasOtherParticipants && (
              <button
                type="button"
                onClick={() => {
                  handlePinForAll()
                  setMobileActionsOpen(false)
                }}
                className="flex w-full items-center gap-2 rounded-lg border border-border px-3 py-2 text-left text-sm text-foreground"
              >
                <Pin className={cn("h-4 w-4", isPinnedForAll && "fill-current text-primary")} />
                <span>{isPinnedForAll ? t("videoTile.unpinForAll") : t("videoTile.pinForAll")}</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                handleMaximize()
                setMobileActionsOpen(false)
              }}
              className="flex w-full items-center gap-2 rounded-lg border border-border px-3 py-2 text-left text-sm text-foreground"
            >
              <Maximize2 className="h-4 w-4" />
              <span>{t("videoTile.fullscreen")}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setVideoFitMode((prev) => (prev === "fit" ? "fill" : "fit"))
                setMobileActionsOpen(false)
              }}
              className="flex w-full items-center gap-2 rounded-lg border border-border px-3 py-2 text-left text-sm text-foreground"
            >
              <Monitor className="h-4 w-4" />
              <span>{videoFitMode === "fit" ? t("videoTile.fitVideo") : t("videoTile.fillVideo")}</span>
            </button>

            {!isAdditionalSource && (
              <button
                type="button"
                onClick={() => {
                  handleOpenChat()
                  setMobileActionsOpen(false)
                }}
                className="flex w-full items-center gap-2 rounded-lg border border-border px-3 py-2 text-left text-sm text-foreground"
              >
                <MessageCircle className="h-4 w-4" />
                <span>{t("videoTile.chat")}</span>
              </button>
            )}

            {/* Person-scoped actions. An additional camera is not a person —
                muting or removing "it" would target its owner by surprise.
                Gated on moderation as well: this sheet carries Remove user.
                Chat sits above it deliberately, since that is not moderation
                and everyone keeps it. */}
            {!participant.isLocal && !isAdditionalSource && canActOnThis && (
              <>
                {/* The same state-based items as the desktop menu. This sheet
                    always offered "Request audio" and "Request video" and never
                    a stop, whatever the person had on. */}
                {(() => {
                  const cameraOn = participant.cameraOn ?? participant.videoOn
                  const item = (label: string, Icon: typeof Mic, run: (() => void) | undefined, danger = false) => (
                    <button
                      type="button"
                      onClick={() => {
                        run?.()
                        setMobileActionsOpen(false)
                      }}
                      className={danger
                        ? "flex w-full items-center gap-2 rounded-lg border border-destructive/30 px-3 py-2 text-left text-sm text-destructive"
                        : "flex w-full items-center gap-2 rounded-lg border border-border px-3 py-2 text-left text-sm text-foreground"}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{label}</span>
                    </button>
                  )
                  return (
                    <>
                      {participant.muted
                        ? item(t("videoTile.requestAudio"), Mic, () => requestAudio?.(participant.id))
                        : item(t("videoTile.stopAudio"), Mic, () => stopAudio?.(participant.id))}
                      {cameraOn
                        ? item(t("videoTile.stopVideo"), Video, () => stopVideo?.(participant.id))
                        : item(t("videoTile.requestVideo"), Video, () => requestVideo?.(participant.id))}
                      {participant.isScreenSharing
                        && item(t("videoTile.stopScreenshare"), MonitorOff, () => stopScreenShare?.(participant.id))}
                      {item(t("videoTile.removeUser"), UserMinus, () => removeParticipant?.(participant.id), true)}
                    </>
                  )
                })()}
              </>
            )}
          </div>
        </BottomSheet>
      )}



      {/* Audio activity visualization */}

      {/* <div className="pointer-events-none absolute left-3 bottom-14 z-20">

        <div className="flex items-end gap-1 rounded-md bg-black/40 px-2 py-1 ring-1 ring-white/10 backdrop-blur-sm">

          {[0, 1, 2, 3, 4].map((i) => (

            <span

              key={i}

              className={cn(

                "w-0.5 rounded-sm bg-card/75",

                isActiveSpeaker ? "animate-pulse" : "opacity-60",

              )}

              style={{

                height: 4 + ((i % 3) + 1) * 2,

                animationDelay: `${i * 120}ms`,

              }}

            />

          ))}

        </div>

      </div> */}





      {/* Phones have no three-dot menu, so without this a full-screen tile has no way back. */}
      {tileIsFullscreen && !shouldShowThreeDotMenu && onMaximize && (
        <button
          type="button"
          onClick={handleMaximize}
          className="absolute z-50 grid h-11 w-11 cursor-pointer place-items-center rounded-full bg-black/75 text-white/90 ring-1 ring-white/20 transition hover:bg-black/85 hover:text-white"
          style={{
            top: "max(12px, env(safe-area-inset-top))",
            right: "max(12px, env(safe-area-inset-right))",
          }}
          title={t("layouts.exitFullscreen")}
          aria-label={t("layouts.exitFullscreen")}
        >
          <Minimize2 className="h-5 w-5" />
        </button>
      )}

      {/* Three dots menu - bottom right */}

      {shouldShowThreeDotMenu && (

        <div className={menuTriggerContainerClass}>

          {/* Full screen sits beside the menu, not inside it: one click, and
              the icon turns into "exit" while this tile owns the screen. */}
          {onMaximize && (
            <button
              type="button"
              onClick={handleMaximize}
              className="grid h-8 w-8 cursor-pointer place-items-center rounded-full bg-black/75 text-white/90 ring-1 ring-white/20 transition hover:bg-black/85 hover:text-white"
              title={tileIsFullscreen ? t("layouts.exitFullscreen") : t("layouts.enterFullscreen")}
              aria-label={tileIsFullscreen ? t("layouts.exitFullscreen") : t("layouts.enterFullscreen")}
              aria-pressed={tileIsFullscreen}
            >
              {tileIsFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
          )}

          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>

            <DropdownMenuTrigger asChild>

              <button

                type="button"

                className="grid h-8 w-8 cursor-pointer place-items-center rounded-full bg-black/75 text-white/90 ring-1 ring-white/20 transition hover:bg-black/85 hover:text-white"

                title={t("videoTile.moreOptions")}

                aria-label={t("videoTile.moreOptions")}

              >

                <MoreVertical className="h-4 w-4" />

              </button>

            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" side="top" className="w-50 max-h-[40vh] overflow-y-auto">
              <div className="px-3 py-1 text-sm font-semibold">
                {isAdditionalSource ? `${participant.name} · ${participant.sourceLabel}` : participant.name}
              </div>
              <DropdownMenuSeparator />

              {/* Volume Control — an additional camera publishes no audio */}
              {!isAdditionalSource && (
                <>
                  <div className="px-3 py-2 flex items-center gap-2">
                    <Volume1 className="h-5 w-5 text-slate-500 dark:text-muted-foreground" />

                    <Slider />
                    <Volume2 className="h-5 w-5 text-slate-500 dark:text-muted-foreground" />
                  </div>
                  <DropdownMenuSeparator />
                </>
              )}

              {/* Main Actions - Pin options */}
              {/* "Pin for all" is the viewer's to offer, as a moderator, on any
                  tile. It was gated on the TILE's role being host and wired to
                  the same handler as "Pin for me", so it only toggled your own
                  pin and on a pinned tile unpinned it. */}
              {onPinForAll && hasOtherParticipants ? (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="gap-2">
                    <Pin className={cn("h-4 w-4", isPinned && "fill-current text-primary")} />
                    <span>{isPinned ? t("videoTile.unpin") : t("videoTile.pin")}</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-48">
                    <DropdownMenuItem onSelect={handlePin} className="gap-2">
                      <Pin className={cn("h-4 w-4", isPinned && "fill-current text-primary")} />
                      {isPinned ? t("videoTile.unpinForMe") : t("videoTile.pinForMe")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={handlePinForAll} className="gap-2">
                      <Pin className={cn("h-4 w-4", isPinnedForAll && "fill-current text-primary")} />
                      {isPinnedForAll ? t("videoTile.unpinForAll") : t("videoTile.pinForAll")}
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              ) : (
                <DropdownMenuItem onSelect={handlePin} className="gap-2">
                  <Pin className={cn("h-4 w-4", isPinned && "fill-current text-primary")} />
                  {isPinned ? t("videoTile.unpinTile") : t("videoTile.pinTile")}
                </DropdownMenuItem>
              )}

              <DropdownMenuItem
                onSelect={() => {
                  setStatsOpen((prev) => !prev)
                  setMenuOpen(false)
                }}
                className="gap-2 justify-between"
              >
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  <span>{t("advancedStats.connection")}</span>
                </div>
                <span className="text-xs text-slate-500 dark:text-muted-foreground">
                  {statsOpen ? t("videoTile.on") : t("videoTile.off")}
                </span>
              </DropdownMenuItem>

              {/* Video Fit Toggle */}
              <DropdownMenuItem
                onSelect={() => {
                  setVideoFitMode((prev) => (prev === "fit" ? "fill" : "fit"))
                  setMenuOpen(false)
                }}
                className="gap-2 justify-between"
              >
                <div className="flex items-center gap-2">
                  <Monitor className="h-4 w-4" />
                  <span>{videoFitMode === "fit" ? t("videoTile.fitVideo") : t("videoTile.fillVideo")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 dark:text-muted-foreground">
                    {videoFitMode === "fit" ? t("videoTile.on") : t("videoTile.off")}
                  </span>
                </div>
              </DropdownMenuItem>

              {/* Person-scoped actions — never on an additional-camera tile, and
                  only for someone allowed to moderate. Without that second test
                  every attendee was shown Stop audio, Stop video and Make
                  moderator for everybody else in the room. */}
              {!participant.isLocal && !isAdditionalSource && canActOnThis && (
                <>
                  <DropdownMenuSeparator />

                  {/* Moderate Actions Section */}
                  <div className="px-3 py-1 text-xs font-semibold text-slate-500 dark:text-muted-foreground uppercase tracking-wider">
                    {t("videoTile.moderateActions")}
                  </div>

                  {/* One control per device, not both: a Stop action on a
                      device that is already off did nothing, so each device
                      offers only the action its current state allows. Camera
                      state reads `cameraOn` (falling back to `videoOn`)
                      because `videoOn` can be forced off by this viewer's own
                      "pause incoming videos" setting without the camera
                      actually being off. */}
                  {participant.muted ? (
                    <DropdownMenuItem
                      onSelect={() => {
                        // Ask THEM to unmute. This used to open HostRequestDialog
                        // on the moderator's own screen (the prompt a participant
                        // is shown when a host asks them to turn something on),
                        // whose Accept only wrote to the console. Nothing ever
                        // reached the other person.
                        requestAudio?.(participant.id)
                        setMenuOpen(false)
                      }}
                      className="gap-2"
                    >
                      <Mic className="h-4 w-4" />
                      <span>{t("videoTile.requestAudio")}</span>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      onSelect={() => {
                        stopAudio?.(participant.id)
                        setMenuOpen(false)
                      }}
                      className="gap-2"
                    >
                      <Mic className="h-4 w-4" />
                      <span>{t("videoTile.stopAudio")}</span>
                    </DropdownMenuItem>
                  )}

                  {(participant.cameraOn ?? participant.videoOn) ? (
                    <DropdownMenuItem
                      onSelect={() => {
                        stopVideo?.(participant.id)
                        setMenuOpen(false)
                      }}
                      className="gap-2"
                    >
                      <Video className="h-4 w-4" />
                      <span>{t("videoTile.stopVideo")}</span>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      onSelect={() => {
                        // Same as Request audio above: this asked nobody anything.
                        requestVideo?.(participant.id)
                        setMenuOpen(false)
                      }}
                      className="gap-2"
                    >
                      <Video className="h-4 w-4" />
                      <span>{t("videoTile.requestVideo")}</span>
                    </DropdownMenuItem>
                  )}

                  {participant.isScreenSharing && (
                    <DropdownMenuItem
                      onSelect={() => {
                        stopScreenShare?.(participant.id)
                        setMenuOpen(false)
                      }}
                      className="gap-2"
                    >
                      <MonitorOff className="h-4 w-4" />
                      <span>{t("videoTile.stopScreenshare")}</span>
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuItem
                    onSelect={() => {
                      removeParticipant?.(participant.id)
                      setMenuOpen(false)
                    }}
                    className="gap-2 text-red-600 focus:text-red-600"
                  >
                    <UserMinus className="h-4 w-4" />
                    {t("videoTile.removeUser")}
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  {/* Change Roles Section. Never on the host: the Moderator
                      switch read ON for them and flipping it demoted the host. */}
                  {participant.role !== "host" && (
                    <>
                    {/* Change Roles Section */}
                    <div className="px-3 py-1 text-xs font-semibold text-slate-500 dark:text-muted-foreground uppercase tracking-wider">
                      {t("videoTile.changeRoles")}
                    </div>

                    <DropdownMenuItem
                      onSelect={() => {
                        changeRole?.(participant.id, isModerator ? "attendee" : "moderator")
                        setMenuOpen(false)
                      }}
                      className="gap-2 justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Crown className="h-4 w-4" />
                        <span>{t("videoTile.moderator")}</span>
                      </div>
                      <div>
                        <Switch checked={isModerator} />
                      </div>
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onSelect={() => {
                        changeRole?.(participant.id, isPresenter ? "attendee" : "presenter")
                        setMenuOpen(false)
                      }}
                      className="gap-2 justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4" />
                        <span>{t("videoTile.presenter")}</span>
                      </div>
                      <div>
                        <Switch checked={isPresenter} />
                      </div>
                    </DropdownMenuItem>

                    {/* Annotation access: its own grant, not part of Presenter.
                        Co-hosts draw without one. */}
                    {!isModerator && setAnnotator && (
                      <DropdownMenuItem
                        onSelect={() => {
                          setAnnotator(participant.id, !participant.annotationAccess)
                          setMenuOpen(false)
                        }}
                        className="gap-2 justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <PenLine className="h-4 w-4" />
                          <span>{t("videoTile.annotation")}</span>
                        </div>
                        <div>
                          <Switch checked={!!participant.annotationAccess} />
                        </div>
                      </DropdownMenuItem>
                    )}
                    </>
                  )}
                </>
              )}
            </DropdownMenuContent>

          </DropdownMenu>

        </div>

      )}



      {/* Avatar — always here, beneath the video rather than swapped for it.

          It used to be mounted only while there was no picture, above the
          video, so every time a tile (re)bound its stream the avatar popped in
          at full strength and the video then faded in behind a hole. Now the
          video fades in OVER it, and only then does the avatar fade out — it
          must, or it would show through the letterbox bars in Fit mode. The
          reverse is instant: the moment there is no picture the avatar is
          simply there, with nothing to fade from.

          A frozen camera is the one case where it goes OVER the video, on the
          tile's own colour. The video is left exactly as it is underneath —
          fading it out instead would make its frame count depend on how a
          browser treats a transparent element, and that count is the only
          thing that can say when the pictures are moving again. */}
      <div
        aria-hidden={(videoVisible && !frozen) || undefined}
        className={cn(
          "pointer-events-none absolute inset-0 flex items-center justify-center p-4",
          // Above the video, below the raised-hand badge (z-10), which sits
          // earlier in the tile and would otherwise be covered.
          frozen
            ? "z-[5] opacity-100"
            : videoVisible ? "opacity-0 transition-opacity duration-200 delay-200" : "opacity-100",
        )}
        style={frozen ? backgroundStyle : undefined}
      >
        <div className="relative w-1/3 max-w-36 min-w-14">
          <div
            className="aspect-square rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(255,255,255,0.3)' }}
          >
            <span className="text-[clamp(1rem,4vw,2rem)] font-medium text-slate-800 dark:text-foreground">
              {initials || "U"}
            </span>
          </div>
        </div>
      </div>

      {/* WebRTC camera stream — shared local capture, primary tile only */}

      {shouldRenderVideo && participant.isLocal && !participant.stream && (

        virtualBackgroundUrl ? (

          <VirtualBackgroundStreamWrapper
            backgroundUrl={virtualBackgroundUrl}
            videoFitMode={videoFitMode}
            tileId={participant.id}
            className={cn(
              "absolute inset-0 transition-opacity duration-300 ease-out",
              videoVisible ? "opacity-100" : "opacity-0",
              mirrorSelf && "scale-x-[-1]",
            )}

            onReady={setHasVideo}

          />

        ) : (

          <CameraStream
            videoFitMode={videoFitMode}
            tileId={participant.id}
            className={cn(
              "absolute inset-0 transition-opacity duration-300 ease-out",
              videoVisible ? "opacity-100" : "opacity-0",
              mirrorSelf && "scale-x-[-1]",
            )}

            onReady={setHasVideo}

          />

        )

      )}

      {/* An explicit stream — every remote tile, plus your own extra cameras */}
      {shouldRenderVideo && participant.stream && (
        <RemoteVideoStream
          stream={participant.stream}
          videoFitMode={videoFitMode}
          tileId={participant.id}
          layerKey={layerKeyForTile(participant)}
          className={cn(
            "absolute inset-0 transition-opacity duration-300 ease-out",
            videoVisible ? "opacity-100" : "opacity-0",
            // This path also carries your own tile once a background is
            // applied, hence the same mirror rule rather than "remote only".
            mirrorSelf && "scale-x-[-1]",
          )}
          onReady={setHasVideo}
          onStalled={watchForStall ? handleStalled : undefined}
        />
      )}



      {/* Emojis float out of the tile of whoever reacted. */}
      <ReactionFloaters emoji={reaction ?? null} />

      {/* Muted mic icon - top right */}
      <div className="absolute right-2 top-2 z-20 flex items-center gap-1 sm:right-3 sm:top-3 sm:gap-2">

        {reaction && (
          <div className="flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-black/45 ring-1 ring-white/15 animate-[reactionWave_1.2s_ease-in-out_infinite]">

            <span className="text-lg">{reaction}</span>

          </div>

        )}

        {!!qualityLabel && (

          <div

            className={cn(

              "flex items-center gap-1 rounded-full bg-black/45 px-2 py-1 text-[11px] font-semibold ring-1",

              participant.connectionQuality === "good"

                ? "text-emerald-200 ring-emerald-400/20"

                : participant.connectionQuality === "fair"

                  ? "text-amber-200 ring-amber-400/20"

                  : "text-red-200 ring-red-400/20",

            )}

            title={t("videoTile.connectionQuality")}

          >

            <span className="inline-flex items-end gap-0.5">

              <span className={cn("h-2 w-0.5 rounded-sm", participant.connectionQuality === "poor" ? "bg-red-300/80" : "bg-card/60")} />

              <span className={cn("h-3 w-0.5 rounded-sm", participant.connectionQuality === "poor" ? "bg-red-300/80" : "bg-card/60")} />

              <span className={cn("h-4 w-0.5 rounded-sm", participant.connectionQuality === "poor" ? "bg-red-300/80" : "bg-card/60")} />

            </span>

            {qualityLabel}

          </div>

        )}



        {/* Stop this extra camera. Only its publisher gets it — nobody else can
            turn off someone else's camera from a tile. */}
        {isAdditionalSource && participant.isLocal && removeVideoInput && participant.sourceId && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              removeVideoInput(participant.sourceId!)
            }}
            className="flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-black/45 text-white ring-1 ring-white/15 transition hover:bg-red-500/80 hover:ring-red-300/40"
            title={t("videoTile.stopVideoInput")}
            aria-label={t("videoTile.stopVideoInput")}
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {/* An extra camera publishes no audio, so a "muted" pill on it is noise. */}
        {participant.muted && !isAdditionalSource && (
          <div className="flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-black/45 ring-1 ring-white/15">

            <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">

              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />

              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />

            </svg>

          </div>

        )}



        {/* Camera off, struck through like the mic pill beside it. It drew a
            plain camera, which read as "camera on" on every tile whose camera
            was off. Keyed on the person's own camera (`cameraOn`), not
            `videoOn`, which this viewer's "pause incoming videos" also turns
            false. */}
        {!(participant.cameraOn ?? participant.videoOn) && (
          <div className="flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-black/45 ring-1 ring-white/15">

            <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">

              <path

                strokeLinecap="round"

                strokeLinejoin="round"

                strokeWidth={2}

                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"

              />

              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />

            </svg>

          </div>

        )}

      </div>






      {/* Name overlay - bottom left with improved UI */}
      <div 
        className={cn(
          "absolute inset-x-0 bottom-0 z-20 px-2 py-1.5 sm:px-3 sm:py-2.5 transition-opacity duration-300",
          showName || !autoHideNames ? "opacity-100" : "opacity-0"
        )}
      >
        <div className="flex items-end justify-between gap-1 sm:gap-2">
          {/* Left side - Name and badges */}
          <div className="min-w-0 flex items-center gap-1 sm:gap-2">
            {/* Name with glass effect background */}
            {/* Opaque rather than blurred: `backdrop-filter` makes the compositor copy,
              blur and recomposite whatever is behind it on EVERY frame the video
              updates, per tile — measured at ~7% of a core across the grid. A more
              solid background reads the same and costs one paint. */}
            <div className="flex items-center gap-1 sm:gap-1.5 rounded-lg bg-black/70 px-1.5 py-0.5 sm:px-2.5 sm:py-0.5 ring-1 ring-white/10 max-w-full">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="truncate text-[11px] sm:text-xs font-medium text-white max-w-[120px] sm:max-w-[160px] md:max-w-[200px] cursor-default">
                    {participant.name}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{participant.name}</p>
                </TooltipContent>
              </Tooltip>

              {participant.isLocal && !isAdditionalSource && (
                <span className="text-[10px] sm:text-xs font-normal text-white/60">({t("videoTile.you")})</span>
              )}

              {/* Device name — takes the role slot on an additional-source tile,
                  so "who + what" reads in one line: "Vivek · Logitech C925e". */}
              {isAdditionalSource ? (
                <>
                  <span className="text-[10px] sm:text-xs font-normal text-white/40">·</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="truncate text-[10px] sm:text-[12px] font-semibold text-sky-100 max-w-[90px] sm:max-w-[130px] cursor-default">
                        {participant.sourceLabel}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{participant.sourceLabel}</p>
                    </TooltipContent>
                  </Tooltip>
                </>
              ) : (
                /* Role badge - separate pill */
                !!roleLabel && (
                  <div className="flex inline-flex justify-center items-center rounded-md py-0.5 sm:py-1 font-semibold text-amber-100">
                    <span className="text-[10px] sm:text-[12px]">{roleLabel}</span>
                  </div>
                )
              )}

              {/* Mic level bars - local participant only */}
              {participant.isLocal && !isAdditionalSource && !participant.muted && (
                <div className="flex items-end gap-[2px] sm:gap-0.5 rounded-md px-1 sm:px-2 py-0.5 sm:py-1">
                  {[0, 1, 2, 3].map((i) => (
                    <span
                      key={i}
                      className="w-[2px] sm:w-[3px] rounded-full bg-card/90 dark:bg-foreground"
                      style={{
                        height: `${3 + (isSpeakingTemp ? 0.6 : 0.1) * 8 + i * 1.5}px`,
                        // `micBars` has no @keyframes anywhere in the app, so this named an
                        // animation that never existed — the bars have always been static.
                        // Left as none rather than inventing a loop nobody asked for.
                        animation: 'none',
                        opacity: isSpeakingTemp ? 0.9 : 0.5,
                      }}
                    />
                  ))}
                </div>
              )}

            </div>







            {/* Pinned indicator */}
            {isPinned && (
              <div className="inline-flex items-center rounded-md bg-blue-500/20 p-1 sm:p-1.5 text-blue-100 ring-1 ring-blue-400/30">
                <Pin className="h-2.5 w-2.5 sm:h-3 sm:w-3 fill-current" />
              </div>
            )}





          </div>



          {/* Right side - Presenting badge */}
          {participant.isScreenSharing && (
            <div className="shrink-0 rounded-md bg-indigo-500/35 px-1.5 sm:px-2.5 py-0.5 sm:py-1.5 text-[10px] sm:text-[11px] font-semibold text-indigo-100 ring-1 ring-indigo-400/30">
              {t("videoTile.presenting")}
            </div>
          )}

        </div>

      </div>

      {/* Host Request Dialogs */}

    </div>

  )

}, (prevProps, nextProps) => {

  // Custom comparison function to prevent unnecessary re-renders

  // Only re-render if these specific props change

  return (

    prevProps.participant.id === nextProps.participant.id &&

    prevProps.participant.name === nextProps.participant.name &&

    // Re-bind the <video> when the remote MediaStream identity changes — i.e. the
    // peer's track set changed (new/replaced tracks). Without this the tile keeps
    // a stale/dead track and renders a blank frame.
    prevProps.participant.stream === nextProps.participant.stream &&

    prevProps.participant.muted === nextProps.participant.muted &&

    prevProps.participant.videoOn === nextProps.participant.videoOn &&

    prevProps.participant.cameraOn === nextProps.participant.cameraOn &&

    prevProps.participant.handRaised === nextProps.participant.handRaised &&

    prevProps.participant.isLocal === nextProps.participant.isLocal &&

    prevProps.participant.isSpeaking === nextProps.participant.isSpeaking &&

    prevProps.participant.isScreenSharing === nextProps.participant.isScreenSharing &&

    prevProps.participant.role === nextProps.participant.role &&

    prevProps.participant.connectionQuality === nextProps.participant.connectionQuality &&

    prevProps.participant.sourceKind === nextProps.participant.sourceKind &&

    prevProps.participant.sourceLabel === nextProps.participant.sourceLabel &&

    prevProps.reaction === nextProps.reaction &&

    prevProps.isPinned === nextProps.isPinned &&

    // "Pin for all" / "Unpin for all": whether it is offered, and which tile it
    // currently names. The handler itself is stable (MeetingScreen reads the
    // current pin through a ref), so its identity is not compared.
    prevProps.pinnedForAllId === nextProps.pinnedForAllId &&

    !!prevProps.onPinForAll === !!nextProps.onPinForAll &&

    prevProps.virtualBackgroundUrl === nextProps.virtualBackgroundUrl &&

    prevProps.showMenu === nextProps.showMenu &&

    prevProps.autoHideNames === nextProps.autoHideNames

  )

})

/**
 * The stats panel for one tile, wired to the real streams.
 *
 * A component rather than inline hooks because the subscription must be tied to
 * the panel being OPEN. `useRtpStats` starts collection when it mounts, so
 * rendering it hidden — or unconditionally with a CSS toggle — would poll every
 * tile in the call for the whole meeting to show nothing.
 *
 * The key is peer + source + kind, not a consumer id: a tile knows who it is
 * showing and which camera, and has no business knowing which consumer carries
 * it. Your own tile has no consumer at all, hence `local`.
 */
function TileStats({ participant }: { participant: Participant }) {
  // Derived in rtp-stats, not here. The copy that used to live at this line
  // read `sfuPeerId ?? id`, which for an extra camera is `${ownerId}::aux1` —
  // so it looked up a key nothing registers and this panel showed "—" for every
  // additional camera, permanently and silently.
  const target = tileStatsTarget(participant)

  const video = useRtpStats(target && statsKey(target.peer, target.sourceType, 'video'))
  // Audio belongs to the person, not to their second camera, so an additional
  // source shows video only rather than borrowing the owner's microphone line.
  const audio = useRtpStats(
    participant.sourceKind === 'additional' || !target ? null : statsKey(target.peer, 'camera', 'audio'),
  )

  return <AdvancedStatsOverlay video={video} audio={audio} />
}
